/**
 * Which raster tiles the map draws, decided by the server's MAPTILER_KEY.
 *
 * Fetched once per page load and shared: Command and Planning each mount their
 * own map, and the offline pack must fetch exactly the URLs the map does or
 * the service worker stores tiles under keys the map never asks for.
 */

import { useEffect, useState } from 'react';
import { api } from '@/api/client';

export interface BasemapConfig {
  provider: 'maptiler' | 'osm';
  /** {z}/{x}/{y} template, any key already in the query string. */
  tileUrl: string;
  attribution: string;
  maxzoom: number;
}

/** Used only when the API cannot be reached, so the map still draws. */
export const FALLBACK_BASEMAP: BasemapConfig = {
  provider: 'osm',
  tileUrl: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '&copy; OpenStreetMap contributors',
  maxzoom: 19,
};

let pending: Promise<BasemapConfig> | null = null;

export function loadBasemap(): Promise<BasemapConfig> {
  pending ??= api.get<BasemapConfig>('/basemap', { logged: false }).then((result) => {
    if (result.ok) return result.data;
    // Retry on the next mount rather than pinning a transient failure to OSM
    // for the rest of the session.
    pending = null;
    return FALLBACK_BASEMAP;
  });
  return pending;
}

/** null until the server has answered. */
export function useBasemapConfig(): BasemapConfig | null {
  const [config, setConfig] = useState<BasemapConfig | null>(null);
  useEffect(() => {
    let live = true;
    void loadBasemap().then((loaded) => {
      if (live) setConfig(loaded);
    });
    return () => {
      live = false;
    };
  }, []);
  return config;
}

/** Test seam. */
export function resetBasemapForTests(): void {
  pending = null;
}
